import { z } from 'zod';

/**
 * Schedule JSON (PLATFORM_EXPANSION.md §2.3). A screen's `schedule` is a 7-day
 * grid of time blocks → playlist or layout, with priority, dated overrides, a
 * default fallback and an emergency push (an override with an expiry, not a
 * deploy). Resolution is pure (`packages/schedule`, X2.1) so server, player and
 * admin preview agree; this file is only the data contract + validation.
 *
 * Time is local to the screen's `timezone`; DST is handled by the resolver, not
 * here. `HH:MM` is 24-hour. Days are 0 (Sunday) – 6 (Saturday), matching the
 * media block's per-item schedule.
 */

export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
/** A block *end* time; also allows the end-of-day sentinel `24:00` (23:59→00:00). */
export const HHMM_END = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;

/** Minutes since local midnight; `24:00` → 1440 (end of day). */
export function hhmmToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/** What a time block or override resolves to. */
export const ScheduleTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('playlist'), playlist_id: z.string().min(1) }),
  z.object({ kind: z.literal('layout'), layout_id: z.string().min(1) })
]);
export type ScheduleTarget = z.infer<typeof ScheduleTargetSchema>;

export const ScheduleBlockSchema = z.object({
  id: z.string().min(1),
  /** Days this block applies to; 0 = Sunday … 6 = Saturday. */
  days: z.array(z.number().int().min(0).max(6)).min(1),
  from: z.string().regex(HHMM),
  /**
   * Exclusive end. Half-open `[from, to)`. `24:00` is the end-of-day sentinel
   * (e.g. `23:00` → `24:00` covers the last hour); a block that would wrap past
   * midnight stays invalid — split it into two blocks.
   */
  to: z.string().regex(HHMM_END),
  target: ScheduleTargetSchema,
  /** Higher wins when blocks overlap. Defaults to 0. */
  priority: z.number().int().default(0),
  label: z.string().max(120).optional()
}).superRefine((block, ctx) => {
  if (hhmmToMinutes(block.from) >= hhmmToMinutes(block.to)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Block end must be after start (split a wrap into two blocks)', path: ['to'] });
  }
});
export type ScheduleBlock = z.infer<typeof ScheduleBlockSchema>;

/** A dated override (holiday, event). Applies to the whole local day. */
export const DateOverrideSchema = z.object({
  /** Local calendar date, YYYY-MM-DD. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  target: ScheduleTargetSchema,
  /** Overrides beat grid blocks on the same day unless a block sets a higher priority. */
  priority: z.number().int().default(0),
  label: z.string().max(120).optional()
});
export type DateOverride = z.infer<typeof DateOverrideSchema>;

/**
 * Emergency push: an override that applies from now (or `starts_at`) until
 * `expires_at`, without a deploy. It is time-bounded by construction — never
 * permanent, so a forgotten push cannot silently become the default.
 */
export const EmergencyPushSchema = z.object({
  target: ScheduleTargetSchema,
  starts_at: z.string().datetime(),
  expires_at: z.string().datetime(),
  priority: z.number().int().default(1000),
  label: z.string().max(120).optional()
}).superRefine((push, ctx) => {
  if (push.starts_at >= push.expires_at) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Emergency push must expire after it starts', path: ['expires_at'] });
  }
});
export type EmergencyPush = z.infer<typeof EmergencyPushSchema>;

export const ScheduleSchema = z.object({
  timezone: z.string().min(1),
  blocks: z.array(ScheduleBlockSchema).default([]),
  overrides: z.array(DateOverrideSchema).default([]),
  /** Used when no block or override matches the instant. */
  fallback: ScheduleTargetSchema,
  /** At most one active push; expiry is mandatory. */
  emergency: EmergencyPushSchema.optional()
});
export type Schedule = z.infer<typeof ScheduleSchema>;
